import React, { type FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'

import type { ReadonlyRecord } from '../../../../utils'

import { StatusType } from '../../../../domain'

import { Caption } from '../Typography'
import { mapTranslationParameters } from './Hooks'

const error: SxProps<Theme> = { backgroundColor: 'error.100', color: 'error.800', borderColor: 'error.200' }
const warning: SxProps<Theme> = { backgroundColor: 'warning.100', color: 'warning.800', borderColor: 'warning.200' }
const success: SxProps<Theme> = { backgroundColor: 'success.100', color: 'success.800', borderColor: 'success.200' }
const grey: SxProps<Theme> = { backgroundColor: 'grey.100', color: 'grey.700', borderColor: 'grey.300' }

const colors: ReadonlyRecord<StatusType, SystemStyleObject<Theme>> = {
    [StatusType.DEAD]: error,
    [StatusType.DISCONNECTED]: error,
    [StatusType.EXITED]: error,
    [StatusType.INVALID]: error,
    [StatusType.OFFLINE]: error,
    [StatusType.UNAVAILABLE]: error,
    [StatusType.UNEXPECTED]: error,
    [StatusType.UNREACHABLE]: error,

    [StatusType.CONNECTING]: warning,
    [StatusType.DEVIATED]: warning,
    [StatusType.DISABLING]: warning,
    [StatusType.DISCONNECTING]: warning,
    [StatusType.ENABLING]: warning,
    [StatusType.RECONNECTING]: warning,
    [StatusType.REENABLING]: warning,
    [StatusType.RESTARTING]: warning,
    [StatusType.ROLLING_BACK]: warning,
    [StatusType.STARTING]: warning,
    [StatusType.STOPPING]: warning,
    [StatusType.UPDATING]: warning,

    [StatusType.ACTIVE]: success,
    [StatusType.AVAILABLE]: success,
    [StatusType.CONNECTED]: success,
    [StatusType.ENABLED]: success,
    [StatusType.NO_DEVIATION]: success,
    [StatusType.ONLINE]: success,
    [StatusType.REACHABLE]: success,
    [StatusType.RUNNING]: success,
    [StatusType.VALID]: success,

    [StatusType.CREATED]: grey,
    [StatusType.DISABLED]: grey,
    [StatusType.INACTIVE]: grey,
    [StatusType.NOT_ENABLED]: grey,
    [StatusType.PAUSED]: grey,
    [StatusType.TERMINATED]: grey,
    [StatusType.UNKNOWN]: grey,
    [StatusType.WAITING]: grey,
}

const statusStyle: SxProps<Theme> = {
    borderRadius: 1,
    borderWidth: 1,
    borderStyle: 'solid',
    px: 2,
    py: 0.5,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
}

const Status: FC<Readonly<{ status: StatusType }>> = ({ status, ...props }) => {
    const [id, values] = mapTranslationParameters(status)

    return <Caption sx={[statusStyle, colors[status]]} id={id} values={values} component="span" {...props} />
}

export default Status
